import { AlbumContentType } from 'common/enums';
import { adaptContentType } from 'helpers/utils';
import { Album } from 'models/album';
import { useState } from 'react';

interface Props {
  album: Album;
  className?: string;
  alt?: string;
  /** Shown when the album has no thumbnail image and no playable video. */
  fallback: React.ReactNode;
}

const PREVIEW_TIME_SECONDS = 10;

const firstVideoUrl = (album: Album): string | null => {
  // Files uploaded via `createDocument` arrive with the outer contentType
  // set to `document`, with the real MIME on the inner clip — same logic
  // adaptContentType uses to pick the right list icon.
  const videoContent = album.contents?.find(
    (c) => adaptContentType(c) === AlbumContentType.video,
  );
  if (!videoContent) return null;
  return videoContent.mediaUrl || videoContent.content?.videoUrl || null;
};

/**
 * Renders an album's thumbnail. Order of preference:
 *   1. `album.thumbnailUrl` if the backend produced one
 *   2. The frame at t=10s of the first video content (via media-fragment URL)
 *   3. The caller-provided `fallback` (icon, etc.)
 */
export const AlbumThumbnail = ({ album, className, alt, fallback }: Props) => {
  const [videoFailed, setVideoFailed] = useState(false);

  if (album.thumbnailUrl) {
    return (
      <img src={album.thumbnailUrl} alt={alt ?? ''} className={className} />
    );
  }

  const videoUrl = firstVideoUrl(album);
  if (videoUrl && !videoFailed) {
    return (
      <video
        // Media-fragment URI tells the browser to seek to that timestamp.
        // For videos shorter than 10s, browsers display the last frame.
        src={`${videoUrl}#t=${PREVIEW_TIME_SECONDS}`}
        className={className}
        preload="metadata"
        muted
        playsInline
        // No controls / autoplay — we just want a single still frame.
        onError={() => setVideoFailed(true)}
        aria-label={alt}
      />
    );
  }

  return fallback;
};
